




































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Header from '@/components/Header.vue';
import { ArticleInteractionBus, emitArticleAutocompleteFocused, emitOpenArticle, emitOpenMyRequest } from '@/common/eventBus';
import loading from '@/components/loading';
import * as PleasedApi from '@/common/api/api.pleased';
import * as env from '@/common/environment';
import { ArticleDetail, ArticleMetrics, ArticleRate, BrandConfig, MetadataRule } from '@/common/api/models.pleased';
import ThemeButton from './ThemeButton.vue';
import ArticleFeedback from './ArticleFeedback.vue';
import RelatedArticles from './RelatedArticles.vue';
import ArticleAutocomplete from './ArticleAutocomplete.vue';
import LikeIcon from '@/components/icons/LikeIcon.vue';
import DislikeIcon from '@/components/icons/DislikeIcon.vue';

import FilledLikeIcon from './icons/FilledLikeIcon.vue';
import FilledDislikeIcon from './icons/FilledDislikeIcon.vue';

import { namespace } from 'vuex-class';
import { buildArticleOption, buildConnectConfig, buildConnectToAgentWithArticleConfig, canConnectToAgent, randomId, checkABTestingPassed, checkAiABTestingPassed } from '@/common/bot/lib';
import { ArticleAction, ArticleInteractionExternEvent, ConnectToAgent } from '@/common/bot/types';
import FaAngleRight from './icons/FaAngleRight.vue';
import MdInfo from './icons/MdInfo.vue';
import { Bridge, platform, sendToParent } from '@/common/bridge';
import { ExternalBotEvent } from '@/common/bot/enum/ExternalBotEvent';
import { CHECKS } from '@/common/bot/flow-frameworks/ChatMessageChecks.static';
import { checkActionSupported, resolveArticleAction } from '@/common/bot/flow-frameworks/ChatMessageActions.static';
import { ChatTriggerActionType, manualTriggerCheck, MatchedChatTrigger } from '@/common/bot/flow-frameworks/chat-trigger';
import { GA_EVENT_CUSTOM_DIMENSION_ENUM, GA_EVENT_NAME_ENUM } from '@/common/gaHelper';
import { PleasedUserInfo } from '@/common/types';
import OpenRequestModal from '@/views/Chatroom/OpenRequestModal.vue';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import { mixins } from 'vue-class-component';
import { analytics, syncLogs, BTN_EVTS, CMP_CONTEXTS } from '@/common/logs';
import { isUserEligibleForAiFlow } from '@/common/ai.utils';

const userModule = namespace('user');
const articleAutocompleteModule = namespace('articleAutocomplete');
const articleMetricsModule = namespace('articleMetrics');

@Component({
  components: {
    Header,
    ThemeButton,
    ArticleFeedback,
    ArticleAutocomplete,
    RelatedArticles,
    LikeIcon,
    DislikeIcon,
    FilledLikeIcon,
    FilledDislikeIcon,
    FaAngleRight,
    MdInfo,
    OpenRequestModal,
  },
  directives: {
    loading,
  },
})
export default class Article extends mixins(LogPageEventMixin) {
  @Prop({ default: '' }) readonly articleId!: string;
  @Prop({ default: '' }) readonly title!: string;
  @Prop({ default: '' }) readonly categoryName!: string;
  @Prop({ default: '' }) readonly body!: string;
  @Prop({ default: '' }) readonly sectionTitle!: string;
  @Prop(Boolean) readonly fetchArticle!: boolean;
  @Prop(Boolean) readonly isFromAutocomplete!: boolean;
  @Prop() readonly sourceMessageId!: string;

  @userModule.State('pleasedUser') pleasedUser!: PleasedUserInfo;
  @userModule.State('isLogin') isLogin!: boolean;
  @userModule.State('userCountry') userCountry!: string;

  @articleAutocompleteModule.State('widgetTitle') widgetTitle!: string;
  @articleMetricsModule.Mutation('setArticleMetrics') setArticleMetrics!: (metrics: ArticleMetrics) => void;
  @articleMetricsModule.Action('saveArticleMetrics') saveArticleMetrics!: (params: { endChat?: boolean, restartChat?: boolean }) => void;

  readonly GA_EVENT_NAME_ENUM = GA_EVENT_NAME_ENUM;
  readonly GA_EVENT_CUSTOM_DIMENSION_ENUM = GA_EVENT_CUSTOM_DIMENSION_ENUM;
  readonly CMP_CONTEXTS = CMP_CONTEXTS;

  $refs!: { container: HTMLDivElement }

  loading = false;
  loadingArticleConfig = false;
  articleParentConnectConfig: ConnectToAgent | null = null;
  showRetry = false;
  fetchedArticle: ArticleDetail | null = null;
  articleRating: ArticleRate | null = null;
  metadataRules: MetadataRule[] = [];
  brandConfig: BrandConfig | null = null;

  articleCheck = {
    showWarning: false,
    warningMessage: '',
  }

  articleStartReadTs = Date.now();

  cleanUps: Function[] = [];

  get ratingOptions () {
    return [
      { value: ArticleRate.HELPFUL, text: this.$t('page.article.feedback.helpful'), icon: 'LikeIcon', filledIcon: 'FilledLikeIcon' },
      { value: ArticleRate.NOT_HELPFUL, text: this.$t('page.article.feedback.not_helpful'), icon: 'DislikeIcon', filledIcon: 'FilledDislikeIcon' },
    ];
  }

  get articleBody () {
    return this.fetchedArticle?.body || this.body;
  }

  get articleTitle () {
    return this.fetchedArticle?.title || this.title;
  }

  get canShowAction () {
    const showWarning = !!this.articleCheck?.showWarning;
    if (!this.articleMetadata.articleAction) return { box: false, button: false, warning: false };
    if (this.articleMetadata.canConnectToSupport) return { box: true, button: true, warning: showWarning };
    return { box: true, button: false, warning: showWarning };
  }

  get [PAGE_TRACKING] () {
    return {
      name: PAGE_EVENTS.ARTICLE,
      selection: this.articleId,
    };
  }

  created () {
    const backListener = () => {
      emitOpenArticle({
        isOpen: false,
      });
      window.removeEventListener('popstate', backListener);
      this.emitArticleInteraction('back');
    };

    // Fetch only on created to avoid refetching on article change
    this.fetchBrandConfig();

    window.addEventListener('popstate', backListener);
    this.cleanUps.push(() => window.removeEventListener('popstate', backListener));
  }

  destroyed () {
    const container = document.getElementById('messageListContainer');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
    this.setAndSaveArticleMetrics();
    this.cleanUps.map(x => x());
  }

  get isAiFlowEnabled () {
    if (!this.brandConfig) return false;

    const { openAiDown = false, abTestPercentage, aiAbTestingEnabled, aiEndUserComeBackTime } = this.brandConfig;

    return isUserEligibleForAiFlow({
      openAiDown,
      aiAbTestingEnabled,
      abTestPercentage,
      aiEndUserComeBackTime,
      userId: this.pleasedUser.userId,
      userLoyaltyTier: this.pleasedUser.userLoyaltyTierBand,
      skipAiChat: this.builtArticleConnectConfig?.skipAiChat,
      enableMetadataRules: this.builtArticleConnectConfig?.enableMetadataRules,
    });
  }

  get builtArticleConnectConfig () {
    if (this.loading || !this.fetchedArticle || !this.articleParentConnectConfig) {
      return null;
    }

    const { id, title, articleConfig } = this.fetchedArticle;

    const articleOption = buildArticleOption({
      ...articleConfig,
      id,
      title,
      category: this.articleCategoryName,
    });

    return buildConnectToAgentWithArticleConfig(
      this.articleParentConnectConfig,
      articleOption
    );
  }

  get connectConfig (): ConnectToAgent | null {
    if (!this.builtArticleConnectConfig) return null;

    const extraFields = {
      abTestingPassed: this.abTestingPassed,
    };

    return {
      ...this.builtArticleConnectConfig,
      ...extraFields,
    };
  }

  get enableMetadataRules () {
    return this.connectConfig?.enableMetadataRules && this.metadataRules.length > 0;
  }

  get abTestingPassed () {
    if (this.loading) return false;

    return checkABTestingPassed({
      userId: this.pleasedUser.userId,
      abValue: this.fetchedArticle?.articleConfig.abValue || '',
      enableAbTesting: this.fetchedArticle?.articleConfig.enableAbTesting || false,
      articleId: this.articleId,
    });
  }

  get articleMetadata () {
    if (this.loading || !this.fetchedArticle || !this.articleParentConnectConfig) {
      return {
        articleAction: null,
        canConnectToSupport: false,
      };
    }

    // We can connect user to AI or Agent
    const canConnectToSupport = this.isAiFlowEnabled || canConnectToAgent(this.connectConfig);

    const articleAction = Boolean(this.fetchedArticle?.articleConfig.action) && checkActionSupported({
      action: this.fetchedArticle.articleConfig.action as string,
    }) && resolveArticleAction({
      action: this.fetchedArticle.articleConfig.action,
      actionMessage: this.fetchedArticle.articleConfig.actionMessage,
      actionTitle: this.fetchedArticle.articleConfig.actionText,
    } as ArticleAction);

    return {
      articleAction,
      canConnectToSupport,
    };
  }

  get articleGAParams () {
    return {
      'event_category': 'Article',
      [GA_EVENT_CUSTOM_DIMENSION_ENUM.CONTACT_SUPPORT_AVAILABLE]: this.articleMetadata.canConnectToSupport,
      [GA_EVENT_CUSTOM_DIMENSION_ENUM.ARTICLE_ACTION_AVAILABLE]: Boolean(this.articleMetadata.articleAction),
      [GA_EVENT_CUSTOM_DIMENSION_ENUM.ARTICLE_OPEN_SOURCE]: this.isFromAutocomplete ? 'search' : 'chatroom',
    };
  }

  get articleCategoryName () {
    return this.fetchedArticle?.category?.titleTranslated || this.fetchedArticle?.category?.title || '';
  }

  goToAction (action: ArticleAction) {
    Bridge.goToAction(action.action);
  }

  setAndSaveArticleMetrics () {
    const duration = Math.floor((Date.now() - this.articleStartReadTs) / 1000);

    this.setArticleMetrics({
      articleId: this.articleId,
      connectToAgent: this.articleMetadata.canConnectToSupport,
      abValue: this.fetchedArticle!.articleConfig.abValue || '',
      duration,
    });

    this.saveArticleMetrics({});
  }

  @Watch('articleId')
  setArticleMetricsOnIdChange () {
    this.setAndSaveArticleMetrics();
  }

  @Watch('articleId', { immediate: true })
  onArticleIdChange () {
    if (this.fetchArticle) {
      this.fetchArticleDetails();
    }

    this.fetchArticleRating();
    this.fetchArticleParentConfig();
    this.fetchMetadataRules();
    this.checkArticleTrigger();
    this.$refs.container && this.$refs.container.scrollTo(0, 0);
  }

  onRetryClick () {
    this.showRetry = false;
    this.fetchArticleDetails();
  }

  onArticleRate (rate: ArticleRate) {
    const gaEvent = rate === ArticleRate.HELPFUL ? GA_EVENT_NAME_ENUM.ARTICLE_HELPFUL : GA_EVENT_NAME_ENUM.ARTICLE_NOT_HELPFUL;
    this.$gtag.event(gaEvent, this.articleGAParams);

    const eventName = rate === ArticleRate.HELPFUL ? BTN_EVTS.ARTICLE_HELPFUL : BTN_EVTS.ARTICLE_NOT_HELPFUL;
    analytics.trackClick({
      name: eventName,
      selection: this.articleId,
    });

    this.articleRating = rate;
    PleasedApi.rateArticle({
      articleId: +this.articleId,
      rating: rate,
    });
  }

  backCallback () {
    this.$gtag.event(this.GA_EVENT_NAME_ENUM.ARTICLE_GO_BACK, this.articleGAParams);
    setTimeout(() => { emitArticleAutocompleteFocused(true); }, 100);
    history.back(); // To trigger popState listener
  }

  onBackClick () {
    analytics.trackClick({
      name: BTN_EVTS.ARTICLE_INTERACT_BACK,
      selection: this.articleId,
    });

    this.backCallback();
  }

  goToConnectSupport () {
    this.$gtag.event(this.GA_EVENT_NAME_ENUM.ARTICLE_CONTACT_SUPPORT, this.articleGAParams);
    analytics.trackClick({
      name: BTN_EVTS.ARTICLE_INTERACT_CONTACT_SUPPORT,
      selection: this.articleId,
    });

    this.emitArticleInteraction('support-request');
    emitOpenArticle({ isOpen: false });
  }

  async goToArticleAction () {
    if (!this.articleMetadata.articleAction) return;

    this.$gtag.event(this.GA_EVENT_NAME_ENUM.ARTICLE_ACTION_USAGE, {
      ...this.articleGAParams,
      [this.GA_EVENT_CUSTOM_DIMENSION_ENUM.ARTICLE_ACTION_TYPE]: this.articleMetadata.articleAction.action,
    });

    analytics.trackClick({
      name: BTN_EVTS.ARTICLE_INTERACT_ACTION,
      selection: this.articleId,
    });

    const isAppPlatform = platform === 'ios' || platform === 'app';
    await syncLogs({ endSession: !isAppPlatform });
    Bridge.goToAction(this.articleMetadata.articleAction.action);
  }

  articleInteracted = false;

  emitArticleInteraction (interaction: ArticleInteractionExternEvent['payload']['interaction']) {
    if (this.articleInteracted || !this.connectConfig) return;
    this.articleInteracted = true;

    if (this.loadingArticleConfig || this.loading) {
      // TODO: wait for loading to complete
    }

    const article = this.fetchedArticle!;

    const { action, ...articleOption } = buildArticleOption({
      ...article.articleConfig,
      id: article.id,
      title: article.title,
      category: this.articleCategoryName,
    });

    ArticleInteractionBus.pub({
      id: 'ArticleInteraction-' + randomId(),
      type: ExternalBotEvent.ARTICLE_PAGE_INTERACTION,
      payload: {
        interaction,
        article: {
          ...articleOption,
          ...(action && checkActionSupported({
            action: this.fetchedArticle!.articleConfig.action!,
          }) ? { action: resolveArticleAction(action) } : {}),
        },
        supportOptions: this.connectConfig,
        sourceMessageId: this.sourceMessageId,
        connectToAI: this.isAiFlowEnabled,
      },
    });
  }

  async fetchArticleDetails () {
    this.loading = true;

    try {
      const articleDetails = await PleasedApi.getArticleDetails.cached({ articleId: this.articleId });
      this.fetchedArticle = articleDetails.data;
      document.title = `Article | ${this.fetchedArticle.enTitle || this.fetchedArticle.title} | ${this.articleId}`;
      window.history.pushState('forward', '', `article?articleId=${this.articleId}`); // For handling Back button
    } catch (error) {
      this.showRetry = true;
    } finally {
      this.loading = false;
    }
  }

  fetchArticleRating () {
    PleasedApi.getArticleRating({ articleId: +this.articleId }).then((res) => {
      this.articleRating = res.data.rating;
    });
  }

  async fetchMetadataRules () {
    if (!this.isLogin) return;
    const rules = await PleasedApi.getMetadataAutomations({ articleId: this.articleId });
    this.metadataRules = rules.data;
  }

  checkArticleTrigger () {
    manualTriggerCheck({
      type: 'ARTICLE',
      id: this.articleId,
      userCountry: this.userCountry,
      triggerEvent: null,
      triggerMessage: null,
    }, matched => {
      if (matched) this.showOverlayTrigger(matched);
    });
  }

  fetchArticleParentConfig () {
    if (this.loadingArticleConfig) return;
    this.loadingArticleConfig = true;

    PleasedApi.getArticleParentConfig.cached({ articleId: this.articleId })
      .then(sectionConfig => buildConnectConfig({ ...sectionConfig.data }))
      .then(config => {
        this.articleParentConnectConfig = config;
        this.runCheck();
      })
      .finally(() => {
        this.loadingArticleConfig = false;
      });
  }

  fetchBrandConfig () {
    PleasedApi.getBrandConfig().then(brandConfig => {
      this.brandConfig = brandConfig.data.brandConfig;
    });
  }

  async runCheck () {
    if (!this.isLogin || !this.fetchedArticle?.articleConfig.checks) return;
    await CHECKS.KYC.check().then(checkPassed => {
      if (checkPassed) {
        this.articleCheck = {
          showWarning: false,
          warningMessage: '',
        };
      } else {
        this.articleCheck = {
          showWarning: true,
          warningMessage: CHECKS.KYC.slots().warning,
        };
      }
    });
  }

  showTriggerModal (trigger: MatchedChatTrigger) {
    analytics.trackPageEvent({
      name: PAGE_EVENTS.MODAL_TRIGGER_POP_UP,
      selection: trigger.chatTriggerId,
      enter: true,
    });

    const trackPageLeave = () => {
      analytics.trackPageEvent({
        name: PAGE_EVENTS.MODAL_TRIGGER_POP_UP,
        selection: trigger.chatTriggerId,
        enter: false,
      });
    };

    const triggerDialog = this.$dialog({
      content: trigger.message,
      className: 'overlay-trigger',
      buttons: [
        {
          name: this.$t('global.continue'),
          callback: () => {
            analytics.trackClick({
              name: BTN_EVTS.MODAL_TRIGGER_POP_UP_CONTINUE,
              selection: trigger.chatTriggerId,
            });

            trackPageLeave();
            trigger.report.continue();
            triggerDialog.close();
          },
        },
        {
          name: this.$t('global.end_chat'),
          class: 'primary',
          callback: async () => {
            analytics.trackClick({
              name: BTN_EVTS.MODAL_TRIGGER_POP_UP_END_CHAT,
              selection: trigger.chatTriggerId,
            });

            trackPageLeave();

            await syncLogs({
              promises: [trigger.report.end()],
            });

            triggerDialog.close();
            sendToParent('CLOSE');
          },
        },
      ],
    });
  }

  showOpenRequestModal (trigger: MatchedChatTrigger) {
    const endChat = async () => {
      await syncLogs({
        promises: [trigger.report.end()],
      });

      triggerDialog.close();
      sendToParent('CLOSE');
    };

    const restartChat = () => {
      this.emitArticleInteraction('restart-chat');
      triggerDialog.close();
      emitOpenArticle({ isOpen: false });
    };

    const goToMyRequests = () => {
      this.emitArticleInteraction('go-to-my-requests');
      triggerDialog.close();
      emitOpenArticle({ isOpen: false });
      emitOpenMyRequest(true);
    };

    const selectedOptions = {
      category: this.articleCategoryName,
      section: undefined,
      article: this.articleTitle,
    };

    const triggerDialog = this.$dialog({
      componentBackground: 'transparent',
      content: <OpenRequestModal
        trigger={trigger}
        selectedOptions={selectedOptions}
        onGoToMyRequest={goToMyRequests}
        onRestartChat={restartChat}
        onEndChat={endChat}
      />,
    });
  }

  showOverlayTrigger (trigger: MatchedChatTrigger) {
    const { actionType } = trigger;

    if (actionType.id === ChatTriggerActionType.SHOW_MODAL) {
      this.showTriggerModal(trigger);
    } else if (actionType.id === ChatTriggerActionType.SHOW_OPEN_REQUEST_MODAL) {
      this.showOpenRequestModal(trigger);
    }
  }

  @Watch('loading')
  @Watch('loadingArticleConfig')
  onLoadingChange () {
    // Once article and parent config loaded
    if (!this.loading && !this.loadingArticleConfig && this.fetchedArticle && this.articleParentConnectConfig) {
      this.$gtag.event(this.GA_EVENT_NAME_ENUM.OPEN_ARTICLE, this.articleGAParams);
    }
  }
}
